<template>
<AppMenuAsPopover offset-x
                  top
                  :card-min-width="330"
                  :card-max-width="330"
                  @open="onMenuOpen"
>
  <template #activator="{props: popOverProps}">
    <v-tooltip location="top"
               transition="fade-transition"
    >
      <template #activator="{ props: tooltipProps }">
        <app-button
          type="icon"
          size="small"
          v-bind="{ ...popOverProps, ...tooltipProps }"
        >
          <app-material-icon name="edit" />
        </app-button>
      </template>
      <span>{{ $t('project.checklist.dialogs.ChecklistStatusDialogEditMenu.editStatusTooltip') }}</span>
    </v-tooltip>
  </template>
  <template #default="{closeMenu}">
    <v-card-title class="editStatus__card__title text-text-primary heading-sm-bold d-flex align-center justify-space-between">
      {{ $t('project.checklist.dialogs.ChecklistStatusDialogEditMenu.editStatus') }}
      <app-button
        type="icon"
        @click="closeMenu"
      >
        <app-material-icon name="close" />
      </app-button>
    </v-card-title>
    <v-card-text class="text--primary pt-4">
      <v-row>
        <v-col>
          <div class="mb-4 heading-xs-bold text-text-secondary">{{$t('project.checklist.dialogs.ChecklistStatusDialogEditMenu.form.statusNameLabel')}}</div>
          <AppTextField v-model="v$.editedStatus.label.$model"
                        class="required"
                        :error-messages="labelErrors"
                        :placeholder="$t('project.checklist.dialogs.ChecklistStatusDialogEditMenu.form.statusNamePlaceholder')"
                        :counter="45"
                        :maxlength="45"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <div class="mb-4 heading-xs-bold text-text-secondary">{{$t('project.checklist.dialogs.ChecklistStatusDialogEditMenu.form.statusCategoryLabel')}}</div>
          <AppSelect v-model="v$.editedStatus.category.$model"
                     :items="taskStatusCategoriesList"
                     item-value="name"
                     item-title="name"
                     :error-messages="categoryErrors"
                     class="required"
          >
            <template #item="{item, props}">
              <v-list-item v-bind="props">
                <template #title>
                  {{ $t(`common.statusCategoryNames.${item.raw.name}`) }}
                </template>
              </v-list-item>
            </template>
            <template #selection="{ item }">
              {{ $t(`common.statusCategoryNames.${item.raw.name}`) }}
            </template>
          </AppSelect>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <div class="mb-4 heading-xs-bold text-text-secondary">{{$t('project.checklist.dialogs.ChecklistStatusDialogEditMenu.form.statusColorLabel')}}</div>
          <ChecklistStatusDialogColorPicker v-model="v$.editedStatus.color.$model"/>
          <span v-if="colorErrors.length"
            class="detail-md-regular text-text-danger">
            {{ colorErrors[0] }}
          </span>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="text-right">
          <div class="d-flex justify-end ga-4">
            <app-button
              type="outlined"
              variant="neutral"
              :disabled="postStatusPending"
              @click="closeMenu"
            >
              {{ $t('common.cancel') }}
            </app-button>
            <app-button
              :loading="postStatusPending"
              @click="editStatus(closeMenu)"
            >
              {{ $t('common.register') }}
            </app-button>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
  </template>
</AppMenuAsPopover>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { mapGetters, mapState } from 'vuex'

import AppMenuAsPopover from '@/common/AppMenuAsPopover.vue'
import AppSelect from '@/common/AppSelect.vue'
import AppTextField from '@/common/AppTextField.vue'
import { formFieldErrors } from '@/common/utils/form'
import ChecklistStatusDialogColorPicker from '@/project/checklist/dialogs/ChecklistStatusDialogColorPicker.vue'

export default {
  name: 'ChecklistStatusDialogEditMenu',
  components: { AppMenuAsPopover, AppTextField, AppSelect, ChecklistStatusDialogColorPicker },
  setup: () => ({ v$: useVuelidate() }),
  props: {
    status: {
      type: Object,
      required: true,
    },
    category: {
      type: String,
      required: true,
    },
  },
  validations: {
    editedStatus: {
      label: { required },
      category: { required },
      color: { required },
    },
  },
  data () {
    return {
      editedStatus: {
        label: this.status.label || '',
        category: this.category,
        color: this.status.color || '',
      },
    }
  },
  computed: {
    ...mapState('checklist', ['postStatusPending']),
    ...mapGetters('checklist', ['taskStatusCategoriesList']),
    labelErrors () {
      return formFieldErrors(
        this.v$.editedStatus.label,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('project.checklist.dialogs.ChecklistStatusDialogEditMenu.form.statusNameLabel') })],
        ],
      )
    },
    categoryErrors () {
      return formFieldErrors(
        this.v$.editedStatus.category,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('project.checklist.dialogs.ChecklistStatusDialogEditMenu.form.statusCategoryLabel') })],
        ],
      )
    },
    colorErrors () {
      return formFieldErrors(
        this.v$.editedStatus.color,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('project.checklist.dialogs.ChecklistStatusDialogEditMenu.form.statusColorLabel') })],
        ],
      )
    },
  },
  methods: {
    editStatus (closeMenuFunction) {
      this.$emit('edit', { ...this.editedStatus, id: this.status.id })
      closeMenuFunction()
    },
    onMenuOpen () {
      this.v$.$reset()
      this.editedStatus = {
        label: this.status.label || '',
        category: this.category,
        color: this.status.color || '',
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

.editStatus {
  &__card {
    &__title {
      border-bottom: get-border();
    }
  }
}
</style>
